import { createFileRoute } from "@tanstack/react-router";
import { useSetPageContext } from "@/hooks/use-page-context";
import { useSession } from "@/hooks/use-session";
import { useTaskFilters } from "@/hooks/use-task-filters";
import { TaskKanbanBoard } from "@/components/tasks/TaskKanbanBoard";
import { TaskFilterBar } from "@/components/tasks/TaskFilterBar";
import { TaskSortControl } from "@/components/tasks/TaskSortControl";
import { CalendarView } from "@/components/tasks/calendar/CalendarView";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export const Route = createFileRoute("/_app/_/tasks/")({
	component: TasksPage,
});

function TasksPage() {
	useSetPageContext({
		page_key: "tasks.index",
		page_title: "Tasks",
		available_actions: ["create_task", "update_task_status", "get_task_list"],
		current_data_summary: null,
	});

	const session = useSession();
	const campaignId = session.campaign?.id;
	const {
		filters,
		listParams,
		calendarFilters,
		hasActiveFilters,
		setSearch,
		toggleStatus,
		togglePriority,
		toggleCategory,
		setSort,
		clearAll,
	} = useTaskFilters();

	if (!campaignId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl">
					<p className="text-muted-foreground">Loading campaign...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-7xl space-y-6">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-2xl font-semibold">Tasks</h1>
						<p className="text-sm text-muted-foreground">
							Your campaign tasks and action items
						</p>
					</div>
				</div>

				{/* Filter Bar -- shared between views */}
				<TaskFilterBar
					filters={filters}
					hasActiveFilters={hasActiveFilters}
					onSearchChange={setSearch}
					onToggleStatus={toggleStatus}
					onTogglePriority={togglePriority}
					onToggleCategory={toggleCategory}
					onClearAll={clearAll}
				/>

				<Tabs defaultValue="kanban">
					<div className="flex items-center justify-between">
						<TabsList>
							<TabsTrigger value="kanban">Kanban</TabsTrigger>
							<TabsTrigger value="calendar">Calendar</TabsTrigger>
						</TabsList>
						{/* Sort control for Kanban view */}
						<TaskSortControl
							sortBy={filters.sortBy}
							sortDir={filters.sortDir}
							onSortChange={setSort}
						/>
					</div>
					<TabsContent value="kanban">
						<TaskKanbanBoard
							campaignId={campaignId}
							filterParams={listParams}
						/>
					</TabsContent>
					<TabsContent value="calendar">
						<CalendarView
							campaignId={campaignId}
							calendarFilters={calendarFilters}
						/>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
}
